// src/features/system/developer/components/video-gallery/VideoPreviewModal.tsx
// ────────────────────────────────────────────────────────────────────────────
// Lazy modal: requests a signed playback url on open, renders <video controls>.
//
// Mounted only while a video is selected — keeps the gallery footprint light
// and avoids signing N URLs up-front (least-privilege).
// ────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { VideoFeedItem } from './types';
import type { UseVideoGalleryReturn } from './useVideoGallery';
import { PlaybackNotReadyError } from './api';
import { formatBytes, formatDuration, statusTone } from './format';
import { MapPinLink } from './MapPinLink';

interface Props {
  item: VideoFeedItem;
  requestDetail: UseVideoGalleryReturn['requestDetail'];
  requestPlayback: UseVideoGalleryReturn['requestPlayback'];
  onClose: () => void;
}

function humanizePlaybackError(msg: string): string {
  const raw = String(msg || '');
  if (raw.includes('playback_not_ready_upload_identity')) {
    return 'This video is finishing on the server. Re-open in a few seconds or wait for processing to complete.';
  }
  if (raw.includes('playback_not_found')) {
    return 'This video could not be found in the current tenant context. Refresh the gallery and try again.';
  }
  return raw || 'Failed to load playback url';
}

export default function VideoPreviewModal({ item, requestDetail, requestPlayback, onClose }: Props) {
  const [detailItem, setDetailItem] = useState<VideoFeedItem | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string>('video/mp4');
  const [error,       setError]       = useState<string | null>(null);
  const [notReady,    setNotReady]    = useState<{ code: PlaybackNotReadyError['code']; attempt: number } | null>(null);
  const notReadyTimerRef = useRef<number | null>(null);
  const NOT_READY_MAX = 5;
  const [buffering,   setBuffering]   = useState(false);
  const [bufferedPct, setBufferedPct] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const displayItem = detailItem ?? item;
  const tone = statusTone(displayItem.status);

  useEffect(() => {
    let cancelled = false;
    setDetailItem(null);
    setPlaybackUrl(null);
    setError(null);
    setNotReady(null);
    setBuffering(false);
    setBufferedPct(0);
    requestDetail(item.video_id)
      .then((res) => {
        if (!cancelled) setDetailItem(res.item);
      })
      .catch(() => {
        // Detail metadata is non-critical; playback can still proceed from the index row.
      });

    const attemptPlayback = (attempt: number): void => {
      requestPlayback(item.video_id)
        .then((res) => {
          if (cancelled) return;
          setPlaybackUrl(res.playback_url);
          setContentType(res.content_type || 'video/mp4');
          setNotReady(null);
        })
        .catch((err) => {
          if (cancelled) return;
          if (err instanceof PlaybackNotReadyError) {
            setNotReady({ code: err.code, attempt });
            if (attempt >= NOT_READY_MAX) return;
            const base = err.retryAfterMs && err.retryAfterMs > 0
              ? err.retryAfterMs
              : [2_500, 6_000, 12_000, 24_000, 48_000][Math.min(attempt - 1, 4)];
            const jitter = (Math.random() * 0.5 - 0.25) * base;
            const delay = Math.max(500, Math.round(base + jitter));
            if (notReadyTimerRef.current !== null) window.clearTimeout(notReadyTimerRef.current);
            notReadyTimerRef.current = window.setTimeout(() => {
              notReadyTimerRef.current = null;
              attemptPlayback(attempt + 1);
            }, delay);
            return;
          }
          setError(humanizePlaybackError(err?.message || ''));
        });
    };
    attemptPlayback(1);

    return () => {
      cancelled = true;
      if (notReadyTimerRef.current !== null) {
        window.clearTimeout(notReadyTimerRef.current);
        notReadyTimerRef.current = null;
      }
    };
  }, [item.video_id, requestDetail, requestPlayback]);

  // ESC to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── Progressive playback wiring ─────────────────────────────────
  // Drive a visible "buffered %" + buffering spinner from the
  // <video> element's native events. HTML5 + HTTP Range (V4 signed
  // URLs natively support it on GCS) gives us byte-range streaming
  // for free — playback starts as soon as enough buffer exists.
  //
  // Stall-recovery (TikTok / YouTube Shorts / IG Reels parity):
  //   A persistent `waiting`/`stalled` event near end-of-stream
  //   (typically ~94% — happens when the moov atom is at the end
  //   of a non-faststart MP4, or when a signed URL expires mid-
  //   playback) is recovered by:
  //     1. Snapshotting `currentTime` to resume in-place.
  //     2. Re-minting the signed URL (`requestPlayback`).
  //     3. Re-assigning `src` + `el.load()` + `seek(currentTime)`.
  //   This is silent to the user; we only show the spinner while
  //   the recovery is in-flight. Capped at 2 retries per session
  //   to avoid flapping if the underlying object is truly broken.
  const stallTimerRef = useRef<number | null>(null);
  const recoveriesRef = useRef(0);
  const recoveringRef = useRef(false);
  const MAX_RECOVERIES = 2;
  const STALL_RECOVERY_MS = 4_000;

  const attemptStallRecovery = useCallback(async () => {
    const el = videoRef.current;
    if (!el || recoveringRef.current) return;
    if (recoveriesRef.current >= MAX_RECOVERIES) return;
    recoveringRef.current = true;
    const resumeAt = el.currentTime;
    try {
      const fresh = await requestPlayback(item.video_id);
      // Replace src only when the URL actually changed (else nothing helpful happens).
      if (fresh?.playback_url) {
        setPlaybackUrl(fresh.playback_url);
        setContentType(fresh.content_type || 'video/mp4');
        // Wait a tick for React to remount <video key={playbackUrl}>, then resume.
        window.setTimeout(() => {
          const v = videoRef.current;
          if (!v) return;
          try {
            v.currentTime = Math.max(0, resumeAt - 0.25);
            void v.play();
          } catch { /* swallow */ }
        }, 50);
      } else {
        // No new URL available — fall back to a soft reload of the existing src.
        try { el.load(); el.currentTime = Math.max(0, resumeAt - 0.25); void el.play(); } catch {}
      }
      recoveriesRef.current += 1;
    } catch {
      // Re-mint failed; degrade gracefully to a soft reload.
      try { el.load(); el.currentTime = Math.max(0, resumeAt - 0.25); void el.play(); } catch {}
    } finally {
      recoveringRef.current = false;
    }
  }, [item.video_id, requestPlayback]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const armStallTimer = () => {
      if (stallTimerRef.current != null) return;
      stallTimerRef.current = window.setTimeout(() => {
        stallTimerRef.current = null;
        const v = videoRef.current;
        if (!v) return;
        // Only escalate to a recovery if we're still NOT playing AND we
        // haven't reached the end. (`ended` fires legitimately at 100%.)
        if (v.paused || v.readyState < 3) {
          void attemptStallRecovery();
        }
      }, STALL_RECOVERY_MS);
    };
    const disarmStallTimer = () => {
      if (stallTimerRef.current != null) {
        window.clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
    };

    const updateBuffered = () => {
      try {
        const dur = el.duration;
        if (!dur || !isFinite(dur) || dur <= 0) return;
        const ranges = el.buffered;
        if (!ranges || ranges.length === 0) return;
        // Use the range covering currentTime (or the latest one).
        let end = 0;
        for (let i = 0; i < ranges.length; i++) {
          if (ranges.end(i) > end) end = ranges.end(i);
        }
        setBufferedPct(Math.min(100, Math.round((end / dur) * 100)));
      } catch {}
    };

    const onWaiting   = () => { setBuffering(true);  armStallTimer(); };
    const onStalled   = () => { setBuffering(true);  armStallTimer(); };
    const onCanPlay   = () => { setBuffering(false); disarmStallTimer(); };
    const onPlaying   = () => { setBuffering(false); disarmStallTimer(); };
    const onProgress  = () => { updateBuffered(); /* progress events imply forward motion → reset timer */ disarmStallTimer(); armStallTimer(); };
    const onLoadedMeta = updateBuffered;
    const onTimeUpdate = () => { updateBuffered(); disarmStallTimer(); armStallTimer(); };
    const onEnded      = () => { setBuffering(false); disarmStallTimer(); };

    el.addEventListener('waiting',     onWaiting);
    el.addEventListener('stalled',     onStalled);
    el.addEventListener('canplay',     onCanPlay);
    el.addEventListener('canplaythrough', onCanPlay);
    el.addEventListener('playing',     onPlaying);
    el.addEventListener('progress',    onProgress);
    el.addEventListener('loadedmetadata', onLoadedMeta);
    el.addEventListener('timeupdate',  onTimeUpdate);
    el.addEventListener('ended',       onEnded);

    return () => {
      disarmStallTimer();
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('stalled', onStalled);
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('canplaythrough', onCanPlay);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('progress', onProgress);
      el.removeEventListener('loadedmetadata', onLoadedMeta);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('ended', onEnded);
    };
  }, [playbackUrl, attemptStallRecovery]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${displayItem.display_name ?? displayItem.scope_key}`}
      onClick={onClose}
    >
      <div
        className="pac-glass-raised rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col
                   border border-neutral-700/60 shadow-2xl shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-800/60">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-neutral-100 truncate" title={displayItem.display_name ?? undefined}>
              {displayItem.display_name ?? (
                <span className="text-rose-300 inline-flex items-center gap-1.5">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current shrink-0" aria-hidden>
                    <path d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 6v2h2v-2h-2z" />
                  </svg>
                  Metadata unavailable
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-neutral-400">
              <span className={['px-1.5 py-px rounded border', tone.text, tone.bg, tone.border].join(' ')}>
                {tone.label}
              </span>
              <span>{displayItem.container || 'video'}</span>
              <span aria-hidden>•</span>
              <span>{formatBytes(displayItem.byte_length)}</span>
              {displayItem.duration_ms > 0 && (
                <>
                  <span aria-hidden>•</span>
                  <span>{formatDuration(displayItem.duration_ms)}</span>
                </>
              )}
              {displayItem.resolution && (
                <>
                  <span aria-hidden>•</span>
                  <span>{displayItem.resolution}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {displayItem.geo && (
              <MapPinLink
                geo={displayItem.geo}
                videoLabel={displayItem.display_name ?? displayItem.scope_key}
                size="md"
              />
            )}
            <button
              type="button"
              onClick={onClose}
              className="pac-glass-btn shrink-0"
              aria-label="Close preview"
            >
              ✕
            </button>
          </div>
        </header>

        {/* Player */}
        <div className="flex-1 min-h-0 bg-black flex items-center justify-center">
          {error ? (
            <div className="p-8 text-center text-rose-300 text-sm">
              <p className="font-semibold">Cannot load preview</p>
              <p className="mt-1 text-neutral-400">{error}</p>
            </div>
          ) : notReady ? (
            // Transient not-ready (404 from BFF): the row exists but the
            // GCS object is still materialising. Render calm chrome with
            // an explicit retry counter so the user understands the card
            // is healing itself; the effect-level timer will swap to
            // playback as soon as `playback_url` is mintable.
            <div className="p-8 flex flex-col items-center justify-center gap-3 text-neutral-500">
              <div className="w-8 h-8 rounded-full border-2 border-neutral-600 border-t-neon_orange-300 animate-spin" />
              <p className="text-xs text-neutral-300">
                {notReady.code === 'playback_not_ready_upload_identity'
                  ? 'Upload is finishing on the server…'
                  : notReady.code === 'playback_not_found'
                    ? 'Finalising in this tenant…'
                    : 'Processing…'}
              </p>
              <p className="text-[10px] text-neutral-500">
                {notReady.attempt >= NOT_READY_MAX
                  ? 'Still finishing — close and reopen in a moment'
                  : `Retrying automatically (attempt ${notReady.attempt}/${NOT_READY_MAX})`}
              </p>
            </div>
          ) : !playbackUrl ? (
            <div className="p-8 flex flex-col items-center justify-center gap-3 text-neutral-500">
              <div className="w-8 h-8 rounded-full border-2 border-neutral-600 border-t-neon_orange-300 animate-spin" />
              <p className="text-xs">Minting signed URL…</p>
            </div>
          ) : (
            <div className="relative w-full">
              <video
                ref={videoRef}
                key={playbackUrl}
                src={playbackUrl}
                controls
                autoPlay
                playsInline
                preload="auto"
                className="w-full max-h-[70vh] object-contain bg-black"
              >
                <source src={playbackUrl} type={contentType} />
              </video>

              {/* Buffered progress strip — sits just above the native
                  controls to telegraph "we have N% downloaded" so the
                  user knows progressive streaming is actively working. */}
              <div
                className="absolute left-0 right-0 bottom-0 pointer-events-none flex flex-col gap-0.5"
                aria-hidden
              >
                <div className="h-0.5 bg-white/10">
                  <div
                    className="h-full bg-neon_orange-300/70 transition-[width] duration-150"
                    style={{ width: `${bufferedPct}%` }}
                  />
                </div>
              </div>

              {/* Buffering overlay — shows when the network can't keep
                  up with the current playhead. */}
              {buffering && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full
                                  bg-black/70 border border-white/20 backdrop-blur-md">
                    <div className="w-3.5 h-3.5 rounded-full border-2 border-neutral-500 border-t-neon_orange-300 animate-spin" />
                    <span className="text-[11px] text-neutral-200">Buffering · {bufferedPct}%</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer / metadata strip */}
        <footer className="px-4 py-2 text-[11px] text-neutral-500 font-mono border-t border-neutral-800/60
                           flex flex-wrap gap-x-4 gap-y-1">
          <span>id: {displayItem.video_id}</span>
          {displayItem.sha256_short && <span>sha: {displayItem.sha256_short}</span>}
          {displayItem.workflow_run_id && <span>run: {displayItem.workflow_run_id}</span>}
          {displayItem.language_code && <span>lang: {displayItem.language_code}</span>}
        </footer>
      </div>
    </div>
  );
}
